<template>
<div class="app-frame" :class="{ 'app-frame--fixed': layoutStore.isFixedWidth }" :style="frameStyle">
    <div v-if="shellMounted" v-show="!overlayRole" class="app-shell">
        <aside v-if="layoutStore.mode === 'three'" class="app-shell__list" :style="{ flexBasis: FOLDER_LIST_WIDTH }">
            <PaneView :route="listRoute" />
        </aside>

        <div class="app-shell__main">
            <SplitPane
                v-show="role !== 'other'"
                v-model:collapsed="layoutStore.fileCollapsed"
                :right-only="layoutStore.fileOnly"
                :default-ratio="SPLIT_PANE.defaultRatio[layoutStore.mode === 'three' ? 'three' : 'two']"
            >
                <template #left>
                    <PaneView :route="folderPaneRoute" :empty="NoFolderSelected" />
                </template>

                <template #right>
                    <PaneView :route="filePaneRoute" :empty="NoFileSelected" />
                </template>
            </SplitPane>

            <PaneView v-if="role === 'other'" :route="current" :cache="false" />
        </div>
    </div>

    <div v-if="overlayRole" class="app-overlay" :class="`app-overlay--${overlayRole}`">
        <div class="app-overlay__page">
            <PaneView :route="current" :cache="false" />
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { FOLDER_LIST_WIDTH, getPageRole, SPLIT_PANE } from '@/config/layout'
import { usePaneRoutes } from '@/composables/usePaneRoutes'
import { useLayoutStore } from '@/stores/layout'
import PaneView from '@/layouts/PaneView.vue'
import SplitPane from '@/layouts/SplitPane.vue'
import NoFileSelected from '@/components/NoFileSelected.vue'
import NoFolderSelected from '@/components/NoFolderSelected.vue'

/**
 * Two (FOLDER_LIST/FOLDER | FILE) or three (FOLDER_LIST | FOLDER | FILE) panes.
 * Pages that are not part of the folders browser replace the FOLDER and FILE panes,
 * fullscreen and dialog pages cover everything.
 */
const layoutStore = useLayoutStore()
const { role, current, listRoute, folderPaneRoute, filePaneRoute } = usePaneRoutes()

const overlayRole = computed(() => role.value === 'fullscreen' || role.value === 'dialog' ? role.value : null)

// The panes fetch user data, so they must not mount while the login page is showing
const shellMounted = ref(false)
watch([role, current], () => {
    if (!shellMounted.value && current.value.matched.length > 0 && !overlayRole.value) {
        shellMounted.value = true
    }
}, { immediate: true })

// also runs for a repeated navigation to the open file, so clicking it again still reveals it
const router = useRouter()
const removeAfterEach = router.afterEach(to => {
    layoutStore.fileOnly = layoutStore.fileCollapsed && getPageRole(to) === 'file'
})
onUnmounted(removeAfterEach)

watch(() => layoutStore.fileCollapsed, collapsed => {
    if (!collapsed) layoutStore.fileOnly = false
})

const frameStyle = computed(() => layoutStore.isFixedWidth ? { maxWidth: `${layoutStore.appWidth}px` } : undefined)
</script>

<style scoped>
.app-frame {
    position: relative;
    width: 100%;
    height: 100dvh;
    margin: 0 auto;
    overflow: hidden;
}

.app-frame--fixed {
    border-inline: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.app-shell {
    display: flex;
    height: 100%;
}

.app-shell__list {
    flex: 0 0 auto;
    min-width: 0;
    border-inline-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.app-shell__main {
    flex: 1 1 0;
    min-width: 0;
}

.app-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background-color: rgb(var(--v-theme-background));
}

.app-overlay__page {
    height: 100%;
}

/* phone-sized card centered on the screen */
.app-overlay--dialog {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(var(--v-theme-on-background), 0.06);
}

.app-overlay--dialog .app-overlay__page {
    width: min(100%, 480px);
    height: min(100%, 860px);
    overflow: hidden;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
</style>
